<template>
  <main class="quiz-page">
    <div class="quiz-shell">
      <v-card v-if="result" dark flat class="result-card" :class="result.passed ? 'is-passed' : 'is-failed'">
        <div v-if="result.passed" class="confetti-wrap" aria-hidden="true">
          <span v-for="index in 24" :key="index" class="confetti" :style="confettiStyle(index)"></span>
        </div>

        <span class="result-icon">
          <v-icon>{{ result.passed ? "mdi-trophy-outline" : "mdi-close-circle-outline" }}</v-icon>
        </span>
        <div class="result-eyebrow">Dein Ergebnis</div>
        <h1>{{ result.passed ? "Bestanden!" : "Noch nicht bestanden" }}</h1>
        <p>
          {{
            result.passed
              ? "Stark! Du hast das Modul erfolgreich abgeschlossen."
              : "Noch fehlen richtige Antworten. Starte einen neuen Versuch."
          }}
        </p>

        <div class="score-card">
          <span>Punktzahl</span>
          <strong>{{ result.score }}%</strong>
          <v-progress-linear :value="result.score" :color="result.passed ? '#009640' : '#f43f5e'" background-color="rgba(255,255,255,0.08)" height="8" rounded />
        </div>

        <v-btn v-if="!result.passed" color="#009640" depressed class="primary-action" @click="restart">
          <v-icon left>mdi-restart</v-icon>
          Nochmal versuchen
        </v-btn>
        <v-btn v-else color="#009640" depressed class="primary-action" @click="$router.push(returnPath)">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ returnLabel }}
        </v-btn>
      </v-card>

      <v-card v-else-if="quiz && currentQuestion" dark flat class="question-card">
        <div class="question-header">
          <div>
            <div class="question-eyebrow">Abschlusstest</div>
            <h1>Frage {{ currentIndex + 1 }} von {{ quiz.questions.length }}</h1>
          </div>
          <v-btn icon class="icon-action" aria-label="Test schliessen" @click="$router.back()"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <div class="progress-row">
          <v-progress-linear :value="progressPercent" color="#009640" background-color="rgba(255,255,255,0.08)" height="8" rounded />
          <span>{{ Math.round(progressPercent) }}%</span>
        </div>

        <section class="question-content">
          <div class="content-eyebrow">Wähle {{ currentQuestion.multiple ? "alle richtigen Antworten" : "die richtige Antwort" }}</div>
          <h2>{{ currentQuestion.question }}</h2>

          <div class="option-list">
            <button
              v-for="(option, index) in currentQuestion.options"
              :key="index"
              type="button"
              class="option-item"
              :class="{ selected: isSelected(index) }"
              :aria-pressed="isSelected(index) ? 'true' : 'false'"
              @click="toggleOption(index)">
              <span class="option-control"><v-icon>{{ isSelected(index) ? "mdi-check" : "mdi-checkbox-blank-outline" }}</v-icon></span>
              <span>{{ option.text }}</span>
            </button>
          </div>
        </section>

        <div class="question-actions">
          <span class="selection-hint">
            <v-icon>mdi-information-outline</v-icon>
            {{ hasSelection ? "Antwort ausgewählt" : "Wähle mindestens eine Antwort" }}
          </span>
          <v-btn
            color="#009640"
            depressed
            class="primary-action"
            :disabled="!hasSelection"
            :loading="submitting"
            @click="nextOrSubmit">
            {{ isLastQuestion ? "Abschliessen" : "Weiter" }}
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </v-card>

      <div v-else class="loading-surface">
        <v-progress-circular v-if="loading" indeterminate color="#58d388" size="48" width="4" />
        <template v-else>
          <span><v-icon>mdi-help-circle-outline</v-icon></span>
          <h1>Test nicht verfügbar</h1>
          <p>Die Fragen konnten nicht geladen werden.</p>
          <v-btn outlined color="#58d388" class="secondary-action" @click="$router.back()">Zurück zum Modul</v-btn>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
import api from "@/api"

export default {
  name: "QuizPlayer",
  data() {
    return {
      quiz: null,
      moduleItem: null,
      currentIndex: 0,
      answers: [],
      result: null,
      submitting: false,
      loading: false,
    }
  },
  computed: {
    isLeadershipModule() {
      return Boolean((this.moduleItem && this.moduleItem.track === "leadership") || this.$route.query.from === "leadership")
    },
    returnPath() {
      return this.isLeadershipModule ? "/akademie/leadership/coaching" : "/akademie/suite"
    },
    returnLabel() {
      return this.isLeadershipModule ? "Zu Leadership Coaching" : "Zur Learning Suite"
    },
    currentQuestion() {
      if (!this.quiz || !this.quiz.questions) return null
      return this.quiz.questions[this.currentIndex]
    },
    currentAnswer() {
      return this.answers.find((answer) => answer.questionIndex === this.currentIndex) || { selectedOptions: [] }
    },
    isLastQuestion() {
      if (!this.quiz || !this.quiz.questions) return false
      return this.currentIndex === this.quiz.questions.length - 1
    },
    progressPercent() {
      if (!this.quiz || !this.quiz.questions || !this.quiz.questions.length) return 0
      return ((this.currentIndex + 1) / this.quiz.questions.length) * 100
    },
    hasSelection() {
      return this.currentAnswer.selectedOptions.length > 0
    },
  },
  mounted() {
    this.loadQuiz()
  },
  methods: {
    async loadQuiz() {
      this.loading = true
      try {
        const moduleItem = await api.akademie.fetchModule(this.$route.params.id)
        this.moduleItem = moduleItem
        if (moduleItem && moduleItem.quiz) this.quiz = moduleItem.quiz
      } catch (error) {
        console.error("Fehler beim Laden des Quiz:", error)
      } finally {
        this.loading = false
      }
    },
    getCurrentAnswer() {
      let answer = this.answers.find((entry) => entry.questionIndex === this.currentIndex)
      if (!answer) {
        answer = { questionIndex: this.currentIndex, selectedOptions: [] }
        this.answers.push(answer)
      }
      return answer
    },
    isSelected(index) {
      return this.currentAnswer.selectedOptions.includes(index)
    },
    toggleOption(index) {
      const selectedOptions = this.getCurrentAnswer().selectedOptions
      if (!this.currentQuestion.multiple) {
        selectedOptions.splice(0, selectedOptions.length, index)
        return
      }
      const selectedIndex = selectedOptions.indexOf(index)
      if (selectedIndex > -1) selectedOptions.splice(selectedIndex, 1)
      else selectedOptions.push(index)
    },
    nextOrSubmit() {
      if (this.isLastQuestion) this.submit()
      else this.currentIndex += 1
    },
    async submit() {
      this.submitting = true
      try {
        this.result = await api.akademie.submitQuiz(this.$route.params.id, this.answers)
      } catch (error) {
        console.error("Fehler beim Senden des Quiz:", error)
      } finally {
        this.submitting = false
      }
    },
    restart() {
      this.currentIndex = 0
      this.answers = []
      this.result = null
    },
    confettiStyle(index) {
      const colors = ["#009640", "#58d388", "#7ee5a6", "#d4d4d8", "#fcd34d"]
      const left = (index * 37) % 100
      const delay = ((index * 113) % 1000) / 1000
      const duration = 2.4 + ((index * 31) % 16) / 10
      const rotation = (index * 47) % 360
      const size = 6 + (index % 6)
      return {
        left: left + "%",
        background: colors[index % colors.length],
        animationDelay: delay + "s",
        animationDuration: duration + "s",
        transform: "rotate(" + rotation + "deg)",
        width: size + "px",
        height: size * 1.6 + "px",
      }
    },
  },
}
</script>

<style scoped>
.quiz-page {
  --surface: rgba(255, 255, 255, 0.035);
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.15), transparent 28rem), #121212;
  font-size: 1rem;
}

.quiz-shell {
  width: 100%;
  max-width: 52rem;
}

.question-card,
.result-card {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1.5rem !important;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.025)) !important;
  box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.28);
}

.question-card {
  padding: 1.5rem;
}

.question-header,
.question-actions,
.progress-row {
  display: flex;
  align-items: center;
}

.question-header,
.question-actions {
  justify-content: space-between;
  gap: 1rem;
}

.question-eyebrow,
.content-eyebrow,
.result-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.question-header h1 {
  margin: 0.1875rem 0 0;
  color: #ffffff;
  font-size: 1.25rem;
  line-height: 1.3;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.progress-row {
  gap: 0.75rem;
  margin: 1.25rem 0;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}

.progress-row span {
  color: #7ee5a6;
  font-size: 0.875rem;
  font-weight: 750;
}

.question-content h2 {
  margin: 0.5rem 0 1.25rem;
  color: #ffffff;
  font-size: 1.75rem;
  line-height: 1.45;
}

.option-list {
  display: grid;
  gap: 0.625rem;
}

.option-item {
  display: grid;
  width: 100%;
  min-height: 3.75rem;
  grid-template-columns: 2.25rem minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.9375rem;
  outline: none;
  color: #e4e4e7;
  background: rgba(0, 0, 0, 0.14);
  cursor: pointer;
  font-size: 1rem;
  line-height: 1.5;
  text-align: left;
  transition: border-color 160ms ease, background-color 160ms ease;
}

.option-item.selected {
  border-color: rgba(0, 150, 64, 0.6);
  color: #ffffff;
  background: rgba(0, 150, 64, 0.12);
}

.option-item:focus-visible {
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.option-control {
  display: grid;
  width: 2.25rem;
  height: 2.25rem;
  place-items: center;
  border-radius: 0.6875rem;
  color: #8f9095;
  background: rgba(255, 255, 255, 0.055);
}

.selected .option-control {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.16);
}

.option-control .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.question-actions {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
}

.selection-hint {
  display: flex;
  align-items: center;
  gap: 0.4375rem;
  color: var(--muted);
  font-size: 0.875rem;
}

.selection-hint .v-icon {
  color: #8f9095;
  font-size: 1.125rem;
}

.primary-action,
.secondary-action {
  min-height: 3rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action {
  color: #ffffff !important;
}

.result-card {
  padding: 2.5rem;
  text-align: center;
}

.result-card.is-passed {
  border-color: rgba(0, 150, 64, 0.42);
  background: linear-gradient(145deg, rgba(0, 150, 64, 0.11), rgba(255, 255, 255, 0.025)) !important;
}

.result-card.is-failed {
  border-color: rgba(244, 63, 94, 0.3);
}

.result-icon {
  position: relative;
  z-index: 1;
  display: inline-grid;
  width: 6rem;
  height: 6rem;
  margin-bottom: 1rem;
  place-items: center;
  border-radius: 1.5rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.15);
}

.is-failed .result-icon {
  color: #ff8d98;
  background: rgba(244, 63, 94, 0.13);
}

.result-icon .v-icon {
  color: currentColor;
  font-size: 3.5rem;
}

.result-card h1 {
  position: relative;
  z-index: 1;
  margin: 0.375rem 0 0;
  color: #ffffff;
  font-size: 2.5rem;
  line-height: 1.2;
}

.result-card > p {
  position: relative;
  z-index: 1;
  margin: 0.625rem 0 0;
  color: #b8b8bc;
  font-size: 1rem;
  line-height: 1.55;
}

.score-card {
  position: relative;
  z-index: 1;
  display: grid;
  max-width: 21rem;
  margin: 1.5rem auto;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: rgba(0, 0, 0, 0.16);
}

.score-card span {
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.score-card strong {
  margin: 0.1875rem 0 0.625rem;
  color: #ffffff;
  font-size: 2rem;
}

.result-card .primary-action {
  position: relative;
  z-index: 1;
}

.confetti-wrap {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.confetti {
  position: absolute;
  top: -1.25rem;
  border-radius: 0.125rem;
  opacity: 0.9;
  animation-name: confetti-fall;
  animation-fill-mode: forwards;
  animation-iteration-count: 1;
  animation-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1);
}

@keyframes confetti-fall {
  0% { top: -1.25rem; opacity: 0; transform: rotate(0deg); }
  10% { opacity: 1; }
  100% { top: 110%; opacity: 0.8; transform: rotate(720deg); }
}

.loading-surface {
  display: flex;
  min-height: 28rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--muted);
  text-align: center;
}

.loading-surface > span {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  place-items: center;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.loading-surface > span .v-icon {
  color: currentColor;
}

.loading-surface h1 {
  margin: 0;
  color: #ffffff;
  font-size: 1.25rem;
}

.loading-surface p {
  margin: 0.375rem 0 1rem;
  font-size: 0.875rem;
}

@media (hover: hover) and (pointer: fine) {
  .option-item:hover {
    border-color: rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.055);
  }
}

@media (prefers-reduced-motion: reduce) {
  .confetti {
    display: none;
  }
}

@media (max-width: 37.4375rem) {
  .quiz-page {
    align-items: flex-start;
    padding: 0.75rem;
  }

  .question-card,
  .result-card {
    padding: 1rem;
    border-radius: 1.125rem !important;
  }

  .question-content h2 {
    font-size: 1.25rem;
  }

  .question-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .selection-hint {
    justify-content: center;
  }

  .primary-action,
  .secondary-action {
    width: 100%;
  }

  .result-card {
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
  }

  .result-card h1 {
    font-size: 1.75rem;
  }
}
</style>
